import { ListChecks, Plus, UserRoundPlus, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useTaskActions } from '../../taskApi'
import { fmtDate } from '../../../shared/format'
import type { ChecklistItem, Project, Task } from '../../types'
import { toastError, useStaffLookup } from '../../ui'

export function TaskChecklist({ project, task, canWrite }: { project: Project; task: Task; canWrite: boolean }) {
  const { t } = useTranslation()
  const { addItem, toggleItem, deleteItem } = useTaskActions(project.id, task.id)
  const { nameOf } = useStaffLookup()
  const [text, setText] = useState('')
  const [assigning, setAssigning] = useState<ChecklistItem | null>(null)
  const done = task.checklist.filter((c) => c.done).length
  const pct = task.checklist.length ? Math.round((done / task.checklist.length) * 100) : 0

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!text.trim()) return
    addItem.mutate(text, { onSuccess: () => setText(''), onError: toastError })
  }

  return (
    <section className="rounded-xl border bg-card shadow-card">
      <header className="flex items-center gap-2 border-b px-4 py-3">
        <ListChecks className="size-4 text-muted-foreground" aria-hidden />
        <h2 className="font-semibold">{t('projects.taskDetail.checklist', { done, total: task.checklist.length })}</h2>
      </header>
      <div className="space-y-3 p-4">
        <div className="h-2 overflow-hidden rounded-full bg-muted" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={t('projects.stats.progress')}>
          <div className="h-full rounded-full bg-primary transition-[width]" style={{ width: `${pct}%` }} />
        </div>
        {task.checklist.length === 0 ? <p className="text-sm text-muted-foreground">{t('projects.taskDetail.noChecklist')}</p> : null}
        <ul className="space-y-1">
          {task.checklist.map((c) => (
            <li key={c.id} className="group flex items-center gap-2 rounded-md px-1 py-1 hover:bg-muted/50">
              <input
                id={`cl-${c.id}`}
                type="checkbox"
                className="size-4 shrink-0 accent-[var(--color-primary)]"
                checked={c.done}
                disabled={!canWrite}
                onChange={() => toggleItem.mutate(c.id, { onError: toastError })}
              />
              <label htmlFor={`cl-${c.id}`} className="min-w-0 flex-1 text-sm">
                <span className={cn(c.done && 'text-muted-foreground line-through')}>{c.text}</span>
                {c.assigneeId || c.dueDate ? (
                  <span className="block text-xs text-muted-foreground">
                    {[c.assigneeId ? nameOf(c.assigneeId) : '', c.dueDate ? fmtDate(c.dueDate) : '', c.done && c.doneBy ? t('projects.checklistAssign.doneBy', { name: nameOf(c.doneBy) }) : '']
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                ) : null}
              </label>
              {canWrite ? (
                <Button size="icon" variant="ghost" className="size-7 opacity-60 group-hover:opacity-100" aria-label={t('projects.checklistAssign.title')} onClick={() => setAssigning(c)}>
                  <UserRoundPlus />
                </Button>
              ) : null}
              {canWrite ? (
                <Button size="icon" variant="ghost" className="size-7 opacity-60 group-hover:opacity-100" aria-label={t('projects.taskDetail.removeItem', { text: c.text })} onClick={() => deleteItem.mutate(c.id, { onError: toastError })}>
                  <X />
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
        {canWrite ? (
          <form onSubmit={submit} className="flex gap-2">
            <Input value={text} onChange={(e) => setText(e.target.value)} maxLength={160} placeholder={t('projects.taskDetail.addItem')} aria-label={t('projects.taskDetail.addItem')} className="h-8" />
            <Button type="submit" size="sm" variant="outline" disabled={!text.trim() || addItem.isPending} aria-label={t('common.add')}>
              <Plus />
            </Button>
          </form>
        ) : null}
      </div>
      {assigning ? <ItemAssignModal project={project} task={task} item={assigning} onClose={() => setAssigning(null)} /> : null}
    </section>
  )
}

/** Maddeyi ayrı bir kişiye (vadeli) atama; atanan kişiye bildirim gider, geçmişe yazılır. */
function ItemAssignModal({ project, task, item, onClose }: { project: Project; task: Task; item: ChecklistItem; onClose: () => void }) {
  const { t } = useTranslation()
  const { assignItem } = useTaskActions(project.id, task.id)
  const { nameOf } = useStaffLookup()
  const [assigneeId, setAssigneeId] = useState(item.assigneeId ?? '')
  const [dueDate, setDueDate] = useState(item.dueDate)
  return (
    <Modal
      open
      size="sm"
      onOpenChange={(o) => !o && onClose()}
      icon={<UserRoundPlus />}
      title={t('projects.checklistAssign.title')}
      description={item.text}
      footer={
        <>
          <Button variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={assignItem.isPending} onClick={() => assignItem.mutate({ itemId: item.id, assigneeId, dueDate }, { onSuccess: onClose, onError: toastError })}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Select
          aria-label={t('projects.checklistAssign.assignee')}
          searchable
          value={assigneeId}
          onChange={setAssigneeId}
          options={[{ value: '', label: t('projects.work.unassigned') }, ...project.team.map((m) => ({ value: m.userId, label: nameOf(m.userId), description: m.title }))]}
        />
        <DateField aria-label={t('projects.checklistAssign.due')} value={dueDate} onChange={setDueDate} max={task.dueDate || undefined} />
      </div>
    </Modal>
  )
}
